import { AlertTriangle, History, X } from 'lucide-react';
import { CustomDatePicker, CustomSelect, ModalBackdrop } from '../common';
import { getPurchaseOrderQuantityStep } from '../../hooks/purchase-orders/purchaseOrderHelpers';
import PurchaseOrderStatusBadge from './PurchaseOrderStatusBadge';

function PurchaseOrderEditModal({
  values,
  errors,
  supplierOptions,
  lansaOptions,
  seaweedTypeOptions,
  warehouseLocationOptions = [],
  stockUnits,
  currentStatus,
  isSaving,
  onChange,
  onOpenHistory,
  onClose,
  onSubmit,
}) {
  const isCompleted = (currentStatus || values.status) === 'Completed';
  const isFinalizedForBalancing = Boolean(values.balancingReadyAt);
  const isSaveLocked = isCompleted || isFinalizedForBalancing;

  function handleSubmit(event) {
    if (isSaveLocked) {
      event.preventDefault();
      return;
    }

    onSubmit(event);
  }

  return (
    <ModalBackdrop backdropClassName="bg-[rgba(33,27,22,0.28)]">
      <div className="mx-auto w-full max-w-3xl rounded-[24px] border border-[var(--theme-border-soft)] bg-[linear-gradient(180deg,var(--theme-surface-warm)_0%,var(--theme-surface-muted)_100%)] shadow-[0_24px_60px_rgba(var(--theme-shadow-rgb),0.16)] sm:rounded-[28px]">
        <div className="flex items-start justify-between gap-4 border-b border-[#efe5dd] px-4 py-4 sm:px-7 sm:py-5">
          <div>
            <p className="m-0 text-[0.78rem] font-semibold uppercase tracking-[0.09em] text-[var(--theme-text-muted)]">
              Purchase Order Module
            </p>
            <h2 className="m-0 mt-2 text-[1.2rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.35rem]">
              Edit Purchase Order
            </h2>
            <p className="m-0 mt-2 text-[0.95rem] text-[var(--theme-text-muted)]">
              Update delivery timing and supplier order details in one place.
            </p>
          </div>

          <button
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#e6dbd0] bg-white text-[#6e6257] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onClose}
            disabled={isSaving}
            aria-label="Close purchase order modal"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <form className="px-4 py-4 sm:px-7 sm:py-6" onSubmit={handleSubmit} noValidate>
          <div className="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3">
            <FormSelect
              label="Supplier"
              name="supplierId"
              value={values.supplierId}
              error={errors.supplierId}
              onChange={onChange}
              options={supplierOptions}
            />
            <FormInput
              label="Sacks Mark"
              name="sacksMark"
              value={values.sacksMark}
              error={errors.sacksMark}
              onChange={onChange}
            />
            <FormSelect
              label="Warehouse"
              name="warehouseLocation"
              value={values.warehouseLocation}
              error={errors.warehouseLocation}
              onChange={onChange}
              options={warehouseLocationOptions}
              placeholder="Select warehouse"
              disabled={!warehouseLocationOptions.length}
            />
            <FormSelect
              label="Unit"
              name="stockUnit"
              value={values.stockUnit}
              error={errors.stockUnit}
              onChange={onChange}
              options={stockUnits}
            />
            <FormInput
              label="Quantity"
              name="quantity"
              type="number"
              value={values.quantity}
              error={errors.quantity}
              onChange={onChange}
              min="0"
              step={getPurchaseOrderQuantityStep(values.stockUnit)}
            />
            <FormSelect
              label="Seaweed Type"
              name="seaweedType"
              value={values.seaweedType}
              error={errors.seaweedType}
              onChange={onChange}
              options={seaweedTypeOptions}
              placeholder="Select seaweed type"
              disabled={!seaweedTypeOptions.length}
            />
            <CustomDatePicker
              label="P.O. Date"
              name="poDate"
              value={values.poDate}
              error={errors.poDate}
              onChange={onChange}
            />
            <CustomDatePicker
              label="Delivery Date"
              name="deliveryDate"
              value={values.deliveryDate}
              error={errors.deliveryDate}
              onChange={onChange}
            />
            <FormInput
              label="Initial Price per Kilo"
              name="price"
              type="number"
              value={values.price}
              error={errors.price}
              onChange={onChange}
              min="0"
              step="0.01"
            />
            <div className="grid gap-1.5">
              <span className="text-[0.74rem] font-medium text-[var(--theme-text-subtle)]">
                Status
              </span>
              <div className="flex min-h-[40px] items-center rounded-full border border-[var(--theme-border)] bg-[var(--theme-surface-warm)] px-3 py-2">
                <PurchaseOrderStatusBadge status={currentStatus || values.status} />
              </div>
              <span className="text-[0.72rem] leading-4 text-[var(--theme-text-muted)]">
                Use the progress workflow to update status.
              </span>
            </div>
            <FormSelect
              label="Lansa Name"
              name="lansaName"
              value={values.lansaName}
              error={errors.lansaName}
              onChange={onChange}
              options={lansaOptions}
            />
          </div>

          <div className="mt-5 flex flex-col gap-2 rounded-[16px] border border-[#e8ddd2] bg-[var(--theme-surface-warm)] px-3 py-2.5 shadow-[0_10px_24px_rgba(var(--theme-shadow-rgb),0.06)] sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="text-[0.78rem] font-semibold text-[var(--theme-text-subtle)]">Current Status</span>
              <PurchaseOrderStatusBadge status={currentStatus || values.status} />
            </div>
            <button
              className="inline-flex min-h-[36px] items-center justify-center gap-1.5 rounded-[12px] border border-[var(--theme-border)] bg-white px-3 text-[0.8rem] font-semibold text-[#5b5048] shadow-[0_6px_14px_rgba(var(--theme-shadow-rgb),0.05)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              type="button"
              onClick={onOpenHistory}
              disabled={isSaving}
            >
              <History className="h-4 w-4" aria-hidden="true" />
              View History Logs
            </button>
          </div>

          <div className="mt-6 grid gap-3 sm:mt-8 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center">
            <button
              className="inline-flex min-h-[46px] items-center justify-center rounded-[14px] border border-[var(--theme-border)] bg-white px-7 text-[0.95rem] font-semibold text-[var(--theme-text-subtle)] shadow-[0_6px_14px_rgba(var(--theme-shadow-rgb),0.05)] outline-none transition hover:bg-[var(--theme-surface-soft)] focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              type="button"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            {isFinalizedForBalancing ? (
              <div className="flex min-h-[46px] items-center gap-2 rounded-[14px] border border-[var(--theme-accent)] bg-[var(--theme-accent-soft)] px-3.5 py-2 text-[0.84rem] font-semibold leading-5 text-[var(--theme-accent-strong)] shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.08)] sm:mx-2">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-white/80 text-[var(--theme-accent-strong)]">
                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  Finalized for Supplier Balancing. Changes are locked.
                </span>
              </div>
            ) : null}
            {!isCompleted ? (
              <button
                className="inline-flex min-h-[46px] items-center justify-center rounded-[14px] border border-transparent bg-[var(--theme-text-subtle)] px-7 text-[0.95rem] font-semibold text-white shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.18)] outline-none transition hover:bg-[var(--theme-accent-strong)] focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:border-[var(--theme-border)] disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-accent-strong)] disabled:opacity-60 disabled:shadow-none sm:justify-self-end"
                type="submit"
                disabled={isSaving || isFinalizedForBalancing}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            ) : null}
          </div>
        </form>
      </div>
    </ModalBackdrop>
  );
}

function FormInput({ label, name, value, error, onChange, type = 'text', min, step }) {
  return (
    <label className="grid gap-1.5">
      <span className="text-[0.74rem] font-medium text-[var(--theme-text-subtle)]">{label}</span>
      <input
        className={`min-h-[40px] rounded-full border bg-[var(--theme-surface-warm)] px-3 py-2 text-[0.84rem] outline-none transition ${
          error
            ? 'border-[#d78b7d] focus:border-[#c76552]'
            : 'border-[var(--theme-border)] focus:border-[var(--theme-text-subtle)]'
        }`}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        min={min}
        step={step}
        inputMode={type === 'number' ? 'decimal' : undefined}
      />
      {error ? <span className="text-[0.74rem] text-[#bb5745]">{error}</span> : null}
    </label>
  );
}

function FormSelect({
  label,
  name,
  value,
  error,
  onChange,
  options,
  placeholder,
  disabled,
  searchable,
}) {
  return (
    <CustomSelect
      label={label}
      name={name}
      value={value}
      error={error}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      searchable={searchable}
    />
  );
}

export default PurchaseOrderEditModal;
